import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import Modal from '../../../../components/Modal/Modal';
import Table from '../../../../components/Table/Table';
import TableHead from '../../../../components/Table/TableHead';
import TableRow from '../../../../components/Table/TableRow';
import TableHeadData from '../../../../components/Table/TableHeadData';
import TableBody from '../../../../components/Table/TableBody';
import TableData from '../../../../components/Table/TableData';
import EmptyTable from '../../../../components/Table/EmptyTable';
import Pagination from '../../../../components/Pagination/Pagination';
import Badge from '../../../../components/Badge/Badge';
import ButtonTypes from '../../../../constants/buttonTypes';
import { authGet } from '../../../../utils/request';

const PAGE_SIZE = 30;

const reasonLabels = {
  part_box: 'Part box',
  done_order: 'Done order',
  release: 'Release',
  cancel: 'Cancel',
  archive: 'Archive',
  check_availability: 'Check availability',
  delivered: 'Delivered',
  missing_parts: 'Missing parts',
  overstock: 'Overstock',
  manual: 'Manual',
};

const formatReason = (reason = '') => reasonLabels[reason]
  || reason.replace(/^cron:/, 'Cron: ').replaceAll('_', ' ');

const formatDate = (date) => DateTime.fromISO(date, { zone: 'utc' })
  .setZone('America/Denver')
  .toFormat('yyyy-MM-dd HH:mm:ss');

const formatDelta = (delta) => `${delta > 0 ? '+' : ''}${delta}`;

const QuantityChange = ({ before, after, delta }) => {
  const changed = delta !== 0;
  return (
    <div className="flex items-center gap-[6px] min-w-max">
      <span>{before}</span>
      <span className="text-text-second">→</span>
      <span>{after}</span>
      {changed ? (
        <Badge
          text={formatDelta(delta)}
          type={delta > 0 ? ButtonTypes.LIGHT_SUCCESS : ButtonTypes.LIGHT_DANGER}
        />
      ) : null}
    </div>
  );
};

QuantityChange.propTypes = {
  before: PropTypes.number,
  after: PropTypes.number,
  delta: PropTypes.number,
};

QuantityChange.defaultProps = {
  before: 0,
  after: 0,
  delta: 0,
};

const WarehouseStockHistoryModal = ({ visible, toggleVisible, item }) => {
  const [history, setHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { _id: warehouseId } = item || {};

  useEffect(() => {
    if (!visible || !warehouseId) return undefined;

    let active = true;
    setLoading(true);
    setError('');

    authGet(`/co/warehouse/${warehouseId}/history`, {
      params: { page: currentPage, size: PAGE_SIZE }
    })
      .then(({ data }) => {
        if (!active) return;
        setHistory(data?.data || []);
        setLastPage(data?.last_page || 1);
      })
      .catch((requestError) => {
        if (!active) return;
        setHistory([]);
        setError(requestError?.data?.message || requestError?.statusText || 'Unable to load stock history.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [visible, warehouseId, currentPage]);

  return (
    <Modal
      visible={visible}
      toggleVisible={toggleVisible}
      title="Stock history"
      subTitle={`${item?.part_whs_brand || ''} ${item?.part_whs_mpn || ''} · ID ${item?.part_whs_id || ''}`}
      hideSaveBtn
    >
      <div className="w-[min(1100px,75vw)] max-w-full">
        {error ? (
          <div className="mb-[15px] rounded-[6px] bg-danger-light p-[12px] text-danger">
            {error}
          </div>
        ) : null}
        <div className="overflow-auto">
          <Table loading={loading}>
            <TableHead>
              <TableRow bordered>
                <TableHeadData>Date (Denver)</TableHeadData>
                <TableHeadData>Reason</TableHeadData>
                <TableHeadData>Total</TableHeadData>
                <TableHeadData>Available</TableHeadData>
                <TableHeadData>Reserved</TableHeadData>
                <TableHeadData>Reference</TableHeadData>
                <TableHeadData>Actor</TableHeadData>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length ? history.map((movement) => {
                const movementData = movement?.data || {};
                const before = movementData?.before || {};
                const after = movementData?.after || {};
                const delta = movementData?.delta || {};
                const applied = movementData?.applied !== false;
                return (
                  <TableRow key={movement.id} bordered>
                    <TableData className="min-w-max">{formatDate(movement.created_at)}</TableData>
                    <TableData>
                      <div className="flex flex-col items-start gap-[4px] min-w-max">
                        <span className="capitalize">{formatReason(movement.reason)}</span>
                        {!applied ? <Badge text="Not applied" type={ButtonTypes.LIGHT_DANGER} /> : null}
                      </div>
                    </TableData>
                    <TableData>
                      <QuantityChange before={before.total} after={after.total} delta={delta.total} />
                    </TableData>
                    <TableData>
                      <QuantityChange
                        before={before.available}
                        after={after.available}
                        delta={delta.available}
                      />
                    </TableData>
                    <TableData>
                      <QuantityChange before={before.reserved} after={after.reserved} delta={delta.reserved} />
                    </TableData>
                    <TableData>
                      <div className="flex flex-col min-w-max">
                        {movement.order_id ? <span>{`Order: ${movement.order_id}`}</span> : null}
                        {movement.ordered_part_id ? <span>{`Ordered part: ${movement.ordered_part_id}`}</span> : null}
                        {!movement.order_id && !movement.ordered_part_id ? <span>—</span> : null}
                      </div>
                    </TableData>
                    <TableData className="min-w-max">{movement.actor || movement.user?.email || '—'}</TableData>
                  </TableRow>
                );
              }) : <EmptyTable colSpan={7} text={error ? 'History unavailable' : 'No stock movements yet'} />}
            </TableBody>
          </Table>
        </div>
        <div className="mt-[15px]">
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            loading={loading}
            onPagePress={setCurrentPage}
          />
        </div>
      </div>
    </Modal>
  );
};

WarehouseStockHistoryModal.propTypes = {
  visible: PropTypes.bool,
  toggleVisible: PropTypes.func,
  item: PropTypes.shape({
    _id: PropTypes.string,
    part_whs_id: PropTypes.number,
    part_whs_brand: PropTypes.string,
    part_whs_mpn: PropTypes.string,
  }),
};

WarehouseStockHistoryModal.defaultProps = {
  visible: false,
  toggleVisible: () => {},
  item: undefined,
};

export default WarehouseStockHistoryModal;
